// src/modules/dashboard/components/WelcomeSection.tsx

import React from 'react';
import Card from '../../../shared/components/ui/Card';
import { User } from '../../../types/user'; // Usar el tipo User correcto
import './WelcomeSection.css';

interface WelcomeSectionProps {
  user: User; // Cambiar UserData por User
}

const WelcomeSection: React.FC<WelcomeSectionProps> = ({ user }) => {
  const getCurrentGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return '🌅 Buenos días';
    if (hour < 18) return '☀️ Buenas tardes';
    return '🌙 Buenas noches';
  };

  const getLastLoginMessage = () => {
    if (!user.last_login) return null;
    
    const lastLogin = new Date(user.last_login);
    const now = new Date();
    const diffMs = now.getTime() - lastLogin.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Último acceso: Hoy';
    } else if (diffDays === 1) {
      return 'Último acceso: Ayer';
    } else if (diffDays < 7) {
      return `Último acceso: Hace ${diffDays} días`;
    } else {
      return `Último acceso: ${lastLogin.toLocaleDateString()}`;
    }
  };

  const getMemberSinceText = () => {
    if (!user.created_at) return 'Fecha no disponible';
    
    return new Date(user.created_at).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long'
    });
  };

  const generateFallbackAvatar = (name: string) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=667eea&color=fff&size=200&bold=true`;
  };

  return (
    <section className="welcome-section">
      <Card className="welcome-card" variant="glass" padding="large">
        <div className="welcome-content">
          <div className="welcome-text">
            <h1 className="welcome-title">
              {getCurrentGreeting()}, {user.name}! 👋
            </h1>
            <p className="welcome-subtitle">
              Bienvenido de vuelta a tu panel de control de iBloom
            </p>
            {getLastLoginMessage() && (
              <p className="last-login">
                {getLastLoginMessage()}
              </p>
            )}
          </div>
          
          <div className="welcome-avatar">
            <img 
              src={user.picture || generateFallbackAvatar(user.name)} 
              alt={user.name}
              className="avatar avatar--xl"
              onError={(e) => {
                (e.target as HTMLImageElement).src = generateFallbackAvatar(user.name);
              }}
            />
            <div className="user-status-badge">
              {user.is_active ? '✅ Activo' : '⚠️ Inactivo'}
            </div>
          </div>
        </div>
        
        <div className="welcome-stats">
          <div className="stat-item">
            <span className="stat-label">Miembro desde</span>
            <span className="stat-value">
              {getMemberSinceText()}
            </span>
          </div>
          
          <div className="stat-item">
            <span className="stat-label">Estado</span>
            <span className={`stat-value ${user.is_active ? 'active' : 'inactive'}`}>
              {user.is_active ? 'Cuenta Activa' : 'Cuenta Inactiva'}
            </span>
          </div>
          
          <div className="stat-item">
            <span className="stat-label">ID de Usuario</span>
            <span className="stat-value user-id">#{user.id}</span>
          </div>
          
          {user.email && (
            <div className="stat-item">
              <span className="stat-label">Email</span>
              <span className="stat-value email">{user.email}</span>
            </div>
          )}
        </div>
        
        {/* Indicador de verificación */}
        <div className="verification-status">
          <div className="verification-item">
            <span className="verification-icon">✅</span>
            <span className="verification-text">Email verificado</span>
          </div>
          
          {user.google_id && (
            <div className="verification-item">
              <span className="verification-icon">🔗</span>
              <span className="verification-text">Conectado con Google</span>
            </div>
          )}
        </div>
      </Card>
    </section>
  );
};

export default WelcomeSection;